// Hook: intercepta tecla BACK (Backspace, Escape, ou keyCodes específicos de TV).
// Cada tela define seu próprio handler. Evita BACK fechar app por engano.

import { useEffect, useRef } from 'react'

const BACK_KEYS = new Set<string>(['Backspace', 'Escape', 'GoBack', 'XF86Back'])
// Keycodes Tizen/webOS pra BACK
const BACK_KEYCODES = new Set<number>([10009, 461, 27, 8])

// Teclas de Voltar DEDICADAS (não ambíguas): valem mesmo com foco num campo
// de texto, porque nunca fazem parte da digitação. Cobre o controle da TV em
// todo webOS/Tizen 2017+ (keyCode 461 Tizen · 10009 webOS) e as variantes de
// `e.key` que alguns WebViews antigos emitem (GoBack/BrowserBack).
const DEDICATED_BACK_KEYS = new Set<string>(['GoBack', 'BrowserBack'])
const DEDICATED_BACK_KEYCODES = new Set<number>([461, 10009])

// Subconjunto estrutural do KeyboardEvent que a decisão precisa. Deixa a
// função pura testável em ambiente node (sem DOM) e chamável tanto pelo
// KeyboardEvent real quanto por um objeto simples nos testes.
export interface BackKeyEventLike {
  key: string
  keyCode: number
  target: EventTarget | null
}

// Decide se o alvo do evento é um campo de texto (input/textarea) de forma
// segura mesmo sem DOM — no ambiente node dos testes `HTMLInputElement` não
// existe, e `instanceof <undefined>` estoura. Cai pra duck-typing (tagName +
// `value`) quando os construtores globais não estão disponíveis.
function isTextTarget(target: EventTarget | null): boolean {
  if (target == null) return false
  const HasInput = typeof HTMLInputElement !== 'undefined'
  const HasTextarea = typeof HTMLTextAreaElement !== 'undefined'
  if (HasInput && target instanceof HTMLInputElement) return true
  if (HasTextarea && target instanceof HTMLTextAreaElement) return true
  if (!HasInput || !HasTextarea) {
    const tag = (target as { tagName?: string }).tagName
    return tag === 'INPUT' || tag === 'TEXTAREA'
  }
  return false
}

// Decisão PURA: esse keydown deve disparar o handler de BACK?
//
// Fora de input/textarea: comportamento clássico (qualquer BACK_KEY/KEYCODE).
//
// Dentro de input/textarea (o D-pad põe o foco real do DOM no <input> — ver
// packages/ui/src/Input.tsx — então o BACK chega com e.target = <input>):
//   - Escape → back.
//   - Teclas de Voltar DEDICADAS (461/10009/GoBack/BrowserBack) → sempre back.
//   - Backspace → back SÓ com o campo vazio (aí é a tecla Voltar do controle).
//     Com texto no campo, Backspace apaga o caractere (uso legítimo do teclado
//     virtual) e NÃO é tratado como back — preserva a digitação.
export function isBackKeyEvent(e: BackKeyEventLike): boolean {
  if (isTextTarget(e.target)) {
    if (e.key === 'Escape') return true
    if (DEDICATED_BACK_KEYS.has(e.key) || DEDICATED_BACK_KEYCODES.has(e.keyCode)) return true
    if (e.key === 'Backspace') {
      // Só é "Voltar" quando não há nada pra apagar.
      return (e.target as { value?: string }).value === ''
    }
    return false
  }

  return BACK_KEYS.has(e.key) || BACK_KEYCODES.has(e.keyCode)
}

export function useBackKey(handler: () => void, enabled = true): void {
  // Ref pra estabilizar o handler — useEffect só re-roda quando `enabled`
  // muda. Sem isso, callers que passam arrow inline (renderizam toda hora)
  // forçariam remove/add do listener a cada render — com janela de race
  // onde o BACK pode passar despercebido.
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!enabled) return

    const onKey = (e: KeyboardEvent) => {
      if (isBackKeyEvent(e)) {
        e.preventDefault()
        handlerRef.current()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
}
